import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Trash2 } from 'lucide-react'

import {
  aanmakenBedrijf, bijwerkenBedrijf, verwijderenBedrijf,
  aanmakenGebruiker, bijwerkenGebruiker, fetchGebruikers,
  fetchLijstCategorie, fetchMutatielog, downloadMutatielogCsvUrl,
  lijstWaardeToevoegen, lijstWaardeVerwijderen,
  resetWachtwoord, verwijderenGebruiker,
  type BedrijfPayload, type GebruikerPayload, type Rol,
} from '@/api/beheer'
import { fetchOrganisations } from '@/api/organisations'
import { Tabs } from '@/components/Tabs'
import { datumNL } from '@/lib/format'

const CATEGORIE_OPTIES = [
  'Crediteur', 'Opslaglocatie', 'Eenheid',
  'Voorraadstatus', 'Orderstatus', 'Overig_categorie',
]

export function BeheerPage() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Beheer</h1>
        <p className="text-sm text-kh-gray-500 mt-1">
          Bedrijven, gebruikers, lijsten, financieringsparameters en mutatielog
        </p>
      </div>

      <Tabs
        tabs={[
          { key: 'bedrijven',     label: 'Bedrijven',              content: <BedrijvenTab /> },
          { key: 'gebruikers',    label: 'Gebruikers',             content: <GebruikersTab /> },
          { key: 'lijsten',       label: 'Dropdown-lijsten',       content: <LijstenTab /> },
          { key: 'parameters',    label: 'Financieringsparameters', content: <ParametersTab /> },
          { key: 'logboek',       label: 'Logboek',                content: <LogboekTab /> },
        ]}
      />
    </div>
  )
}


// ───────────────────────────────────────────────────────────────
// Bedrijven
// ───────────────────────────────────────────────────────────────
function BedrijvenTab() {
  const qc = useQueryClient()
  const { data: orgs, isLoading } = useQuery({ queryKey: ['organisations'], queryFn: fetchOrganisations })
  const [form, setForm] = useState<BedrijfPayload>({
    naam: '', kvk: '', contactpersoon: '', email: '', bederfelijke_waar: 'Ja',
    fin_pct_voorraad: 80, fin_pct_emballage: 70, fin_pct_verpakking: 70, fin_pct_overig: 60,
    min_houdbaarheidsdagen: 180, actief: true,
  })
  const [msg, setMsg] = useState<string | null>(null)

  const aanmaken = useMutation({
    mutationFn: () => aanmakenBedrijf(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organisations'] })
      setMsg('✓ Bedrijf aangemaakt')
      setForm({ ...form, naam: '', kvk: '', contactpersoon: '', email: '' })
    },
    onError: (e: any) => setMsg('Fout: ' + (e?.response?.data?.detail ?? e.message)),
  })

  const verwijderen = useMutation({
    mutationFn: (id: number) => verwijderenBedrijf(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organisations'] }),
  })

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h3 className="font-semibold text-kh-blue mb-4">Nieuw bedrijf aanmaken</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Bedrijfsnaam *">
            <input className="input-field" value={form.naam}
                   onChange={(e) => setForm({ ...form, naam: e.target.value })} />
          </Field>
          <Field label="KvK-nummer">
            <input className="input-field" value={form.kvk ?? ''}
                   onChange={(e) => setForm({ ...form, kvk: e.target.value })} />
          </Field>
          <Field label="Contactpersoon">
            <input className="input-field" value={form.contactpersoon ?? ''}
                   onChange={(e) => setForm({ ...form, contactpersoon: e.target.value })} />
          </Field>
          <Field label="E-mail">
            <input className="input-field" type="email" value={form.email ?? ''}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Bederfelijke waar">
            <select className="input-field" value={form.bederfelijke_waar}
                    onChange={(e) => setForm({ ...form, bederfelijke_waar: e.target.value })}>
              <option>Ja</option><option>Gedeeltelijk</option><option>Nee</option>
            </select>
          </Field>
          <Field label="Min. houdbaarheid (dagen)">
            <input className="input-field" type="number" value={form.min_houdbaarheidsdagen ?? 180}
                   onChange={(e) => setForm({ ...form, min_houdbaarheidsdagen: Number(e.target.value) })} />
          </Field>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button className="btn-primary" disabled={!form.naam.trim() || aanmaken.isPending}
                  onClick={() => { setMsg(null); aanmaken.mutate() }}>
            Bedrijf aanmaken
          </button>
          {msg && <span className="text-sm text-kh-gray-600">{msg}</span>}
        </div>
      </section>

      <section>
        <h3 className="font-semibold text-kh-blue mb-3">Overzicht</h3>
        {isLoading ? <Box>Laden…</Box>
         : !orgs || orgs.length === 0 ? <Box>Nog geen bedrijven.</Box>
         : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <Thead cols={['Bedrijf', 'KvK', 'Contactpersoon', 'Bederfelijk', 'Actief', '']} />
              <tbody className="divide-y divide-kh-gray-100">
                {orgs.map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-2.5 font-medium">{o.naam}</td>
                    <td className="px-4 py-2.5">{o.kvk ?? '—'}</td>
                    <td className="px-4 py-2.5">{o.contactpersoon ?? '—'}</td>
                    <td className="px-4 py-2.5">{o.bederfelijke_waar}</td>
                    <td className="px-4 py-2.5">{o.actief ? '✓' : '✗'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button className="text-red-600 hover:text-red-800"
                              onClick={() => confirm(`Bedrijf '${o.naam}' verwijderen?`) && verwijderen.mutate(o.id)}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Gebruikers
// ───────────────────────────────────────────────────────────────
function GebruikersTab() {
  const qc = useQueryClient()
  const { data: orgs } = useQuery({ queryKey: ['organisations'], queryFn: fetchOrganisations })
  const { data: gebruikers, isLoading } = useQuery({ queryKey: ['gebruikers'], queryFn: fetchGebruikers })

  const [form, setForm] = useState<GebruikerPayload>({
    gebruikersnaam: '', volledige_naam: '', email: '', wachtwoord: '',
    rol: 'user', organisation_id: null, actief: true,
  })
  const [msg, setMsg] = useState<string | null>(null)
  const [credentialsBox, setCredentialsBox] = useState<{ user: string; pw: string; org: string } | null>(null)

  const aanmaken = useMutation({
    mutationFn: () => aanmakenGebruiker(form),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gebruikers'] })
      const orgNaam = orgs?.find((o) => o.id === form.organisation_id)?.naam ?? '—'
      setCredentialsBox({ user: form.gebruikersnaam, pw: form.wachtwoord, org: orgNaam })
      setMsg('✓ Gebruiker aangemaakt')
      setForm({ gebruikersnaam: '', volledige_naam: '', email: '', wachtwoord: '',
                rol: 'user', organisation_id: form.organisation_id, actief: true })
    },
    onError: (e: any) => setMsg('Fout: ' + (e?.response?.data?.detail ?? e.message)),
  })

  const toggleActief = useMutation({
    mutationFn: ({ id, actief }: { id: number; actief: boolean }) => bijwerkenGebruiker(id, { actief }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gebruikers'] }),
  })

  const verwijderen = useMutation({
    mutationFn: (id: number) => verwijderenGebruiker(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gebruikers'] }),
  })

  const resetPw = useMutation({
    mutationFn: ({ id, pw }: { id: number; pw: string }) => resetWachtwoord(id, pw),
    onSuccess: () => alert('✓ Wachtwoord gereset'),
  })

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h3 className="font-semibold text-kh-blue mb-4">Nieuwe gebruiker</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Gebruikersnaam *">
            <input className="input-field" value={form.gebruikersnaam}
                   onChange={(e) => setForm({ ...form, gebruikersnaam: e.target.value })} />
          </Field>
          <Field label="Volledige naam">
            <input className="input-field" value={form.volledige_naam ?? ''}
                   onChange={(e) => setForm({ ...form, volledige_naam: e.target.value })} />
          </Field>
          <Field label="E-mail">
            <input className="input-field" type="email" value={form.email ?? ''}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Wachtwoord * (minstens 8 tekens)">
            <input className="input-field" type="text" value={form.wachtwoord}
                   onChange={(e) => setForm({ ...form, wachtwoord: e.target.value })} />
          </Field>
          <Field label="Organisatie">
            <select className="input-field" value={form.organisation_id ?? ''}
                    onChange={(e) => setForm({ ...form, organisation_id: e.target.value ? Number(e.target.value) : null })}>
              <option value="">— selecteer —</option>
              {orgs?.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
            </select>
          </Field>
          <Field label="Rol">
            <select className="input-field" value={form.rol}
                    onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </Field>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button className="btn-primary"
                  disabled={!form.gebruikersnaam || form.wachtwoord.length < 8 || aanmaken.isPending}
                  onClick={() => { setMsg(null); setCredentialsBox(null); aanmaken.mutate() }}>
            Gebruiker aanmaken
          </button>
          {msg && <span className="text-sm text-kh-gray-600">{msg}</span>}
        </div>
        {credentialsBox && (
          <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 p-3">
            <p className="text-sm font-medium text-yellow-800">Geef deze gegevens door aan de gebruiker:</p>
            <pre className="mt-2 text-xs bg-white border border-yellow-100 rounded p-3 text-kh-gray-700 select-all">
{`App:           https://voorraad.kapitaalhuys.nl
Gebruikersnaam: ${credentialsBox.user}
Wachtwoord:     ${credentialsBox.pw}
Organisatie:    ${credentialsBox.org}`}
            </pre>
          </div>
        )}
      </section>

      <section>
        <h3 className="font-semibold text-kh-blue mb-3">Overzicht</h3>
        {isLoading ? <Box>Laden…</Box>
         : !gebruikers || gebruikers.length === 0 ? <Box>Nog geen gebruikers.</Box>
         : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <Thead cols={['Gebruiker', 'Naam', 'E-mail', 'Organisatie', 'Rol', 'Actief', '']} />
              <tbody className="divide-y divide-kh-gray-100">
                {gebruikers.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-2.5 font-medium">{u.gebruikersnaam}</td>
                    <td className="px-4 py-2.5">{u.volledige_naam ?? '—'}</td>
                    <td className="px-4 py-2.5">{u.email ?? '—'}</td>
                    <td className="px-4 py-2.5">{u.organisation_naam ?? '—'}</td>
                    <td className="px-4 py-2.5 capitalize">{u.rol}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => toggleActief.mutate({ id: u.id, actief: !u.actief })}
                              className={u.actief ? 'text-green-600' : 'text-red-600'}>
                        {u.actief ? '✓ actief' : '✗ inactief'}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-2">
                      <button className="text-xs text-kh-blue hover:underline"
                              onClick={() => {
                                const pw = prompt('Nieuw wachtwoord (min. 8 tekens):') ?? ''
                                if (pw.length >= 8) resetPw.mutate({ id: u.id, pw })
                              }}>
                        wachtwoord
                      </button>
                      <button className="text-red-600 hover:text-red-800"
                              onClick={() => confirm(`Gebruiker '${u.gebruikersnaam}' verwijderen?`) && verwijderen.mutate(u.id)}>
                        <Trash2 size={14} className="inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Lijsten
// ───────────────────────────────────────────────────────────────
function LijstenTab() {
  const qc = useQueryClient()
  const { data: orgs } = useQuery({ queryKey: ['organisations'], queryFn: fetchOrganisations })
  const [orgId, setOrgId] = useState<number | null>(null)
  const [cat, setCat] = useState(CATEGORIE_OPTIES[0])
  const [nieuw, setNieuw] = useState('')

  if (!orgId && orgs && orgs.length > 0 && orgId === null) {
    setOrgId(orgs[0].id)
  }

  const { data: waardes, isLoading } = useQuery({
    queryKey: ['lijst', orgId, cat],
    queryFn: () => fetchLijstCategorie(orgId!, cat),
    enabled: !!orgId,
  })

  const toevoegen = useMutation({
    mutationFn: () => lijstWaardeToevoegen(orgId!, cat, nieuw.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lijst', orgId, cat] }); setNieuw('') },
  })
  const verwijderen = useMutation({
    mutationFn: (id: number) => lijstWaardeVerwijderen(orgId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lijst', orgId, cat] }),
  })

  if (!orgs || orgs.length === 0) return <Box>Maak eerst een bedrijf aan.</Box>

  return (
    <div className="space-y-6">
      <div className="card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Organisatie">
          <select className="input-field" value={orgId ?? ''}
                  onChange={(e) => setOrgId(Number(e.target.value))}>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
          </select>
        </Field>
        <Field label="Categorie">
          <select className="input-field" value={cat} onChange={(e) => setCat(e.target.value)}>
            {CATEGORIE_OPTIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <section className="card p-5">
        <h3 className="font-semibold text-kh-blue mb-3">Waardes voor "{cat}"</h3>
        <div className="flex gap-2 mb-4">
          <input className="input-field flex-1" placeholder="Nieuwe waarde…"
                 value={nieuw} onChange={(e) => setNieuw(e.target.value)} />
          <button className="btn-primary" disabled={!nieuw.trim() || !orgId}
                  onClick={() => toevoegen.mutate()}>
            Toevoegen
          </button>
        </div>
        {isLoading ? <Box>Laden…</Box>
         : !waardes || waardes.length === 0 ? <Box>Geen waardes. Standaardwaardes worden gebruikt.</Box>
         : (
          <ul className="space-y-1">
            {waardes.map((w) => (
              <li key={w.id} className="flex justify-between items-center px-3 py-1.5 rounded hover:bg-kh-gray-50">
                <span>{w.waarde}</span>
                <button className="text-red-600 hover:text-red-800"
                        onClick={() => confirm(`'${w.waarde}' verwijderen?`) && verwijderen.mutate(w.id)}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
         )}
      </section>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Financieringsparameters
// ───────────────────────────────────────────────────────────────
function ParametersTab() {
  const qc = useQueryClient()
  const { data: orgs } = useQuery({ queryKey: ['organisations'], queryFn: fetchOrganisations })
  const [orgId, setOrgId] = useState<number | null>(null)
  const [form, setForm] = useState<BedrijfPayload>({} as BedrijfPayload)
  const [msg, setMsg] = useState<string | null>(null)

  if (!orgId && orgs && orgs.length > 0 && orgId === null) {
    setOrgId(orgs[0].id)
  }
  const org = orgs?.find((o) => o.id === orgId)
  // Sync formstate bij wisselen
  if (org && (form as any)._loadedFor !== org.id) {
    setForm({
      naam: org.naam,
      fin_pct_voorraad: org.fin_pct_voorraad,
      fin_pct_emballage: org.fin_pct_emballage,
      fin_pct_verpakking: org.fin_pct_verpakking,
      fin_pct_overig: org.fin_pct_overig,
      min_houdbaarheidsdagen: org.min_houdbaarheidsdagen,
      ...({_loadedFor: org.id} as any),
    } as any)
  }

  const opslaan = useMutation({
    mutationFn: () => bijwerkenBedrijf(orgId!, {
      fin_pct_voorraad: form.fin_pct_voorraad,
      fin_pct_emballage: form.fin_pct_emballage,
      fin_pct_verpakking: form.fin_pct_verpakking,
      fin_pct_overig: form.fin_pct_overig,
      min_houdbaarheidsdagen: form.min_houdbaarheidsdagen,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['organisations'] }); setMsg('✓ Opgeslagen') },
    onError: (e: any) => setMsg('Fout: ' + (e?.response?.data?.detail ?? e.message)),
  })

  if (!orgs || orgs.length === 0) return <Box>Maak eerst een bedrijf aan.</Box>

  return (
    <div className="space-y-6">
      <Field label="Organisatie">
        <select className="input-field max-w-md" value={orgId ?? ''}
                onChange={(e) => { setOrgId(Number(e.target.value)); setMsg(null) }}>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
        </select>
      </Field>

      <section className="card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <PctField label="Voorraad (%)"      value={form.fin_pct_voorraad ?? 80}
                  onChange={(v) => setForm({ ...form, fin_pct_voorraad: v })} />
        <PctField label="Emballage (%)"     value={form.fin_pct_emballage ?? 70}
                  onChange={(v) => setForm({ ...form, fin_pct_emballage: v })} />
        <PctField label="Verpakkingen (%)"  value={form.fin_pct_verpakking ?? 70}
                  onChange={(v) => setForm({ ...form, fin_pct_verpakking: v })} />
        <PctField label="Overig (%)"        value={form.fin_pct_overig ?? 60}
                  onChange={(v) => setForm({ ...form, fin_pct_overig: v })} />
        <Field label="Min. houdbaarheid (dagen)">
          <input className="input-field" type="number" value={form.min_houdbaarheidsdagen ?? 180}
                 onChange={(e) => setForm({ ...form, min_houdbaarheidsdagen: Number(e.target.value) })} />
        </Field>
      </section>

      <div className="flex items-center gap-3">
        <button className="btn-primary" disabled={opslaan.isPending} onClick={() => { setMsg(null); opslaan.mutate() }}>
          Opslaan
        </button>
        {msg && <span className="text-sm text-kh-gray-600">{msg}</span>}
      </div>
    </div>
  )
}

function PctField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <Field label={label}>
      <input type="number" min={0} max={100} className="input-field"
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  )
}

// ───────────────────────────────────────────────────────────────
// Logboek
// ───────────────────────────────────────────────────────────────
function LogboekTab() {
  const { data: orgs } = useQuery({ queryKey: ['organisations'], queryFn: fetchOrganisations })
  const [orgId, setOrgId] = useState<number | undefined>()
  const [zoek, setZoek] = useState('')

  const { data: logs, isLoading } = useQuery({
    queryKey: ['mutatielog', orgId, zoek],
    queryFn: () => fetchMutatielog({ org_id: orgId, zoek: zoek || undefined, limit: 500 }),
  })

  return (
    <div className="space-y-4">
      <div className="card p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Organisatie">
          <select className="input-field" value={orgId ?? ''}
                  onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">Alle (superadmin)</option>
            {orgs?.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
          </select>
        </Field>
        <Field label="🔍 Zoek">
          <input className="input-field" placeholder="bv. partijnr, gebruiker, detail"
                 value={zoek} onChange={(e) => setZoek(e.target.value)} />
        </Field>
        <div className="flex items-end">
          <a href={downloadMutatielogCsvUrl(orgId)} download
             className="btn-secondary inline-flex items-center gap-2">
            <Download size={16} /> Download CSV
          </a>
        </div>
      </div>

      {isLoading ? <Box>Laden…</Box>
       : !logs || logs.length === 0 ? <Box>Geen mutaties.</Box>
       : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <Thead cols={['Tijdstip', 'Gebruiker', 'Pagina', 'Actie', 'Entiteit', 'Identificatie', 'Detail']} />
            <tbody className="divide-y divide-kh-gray-100">
              {logs.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">{datumNL(m.tijdstip)} {new Date(m.tijdstip).toLocaleTimeString('nl-NL')}</td>
                  <td className="px-4 py-2">{m.gebruiker ?? '—'}</td>
                  <td className="px-4 py-2">{m.pagina ?? '—'}</td>
                  <td className="px-4 py-2">{m.actie ?? '—'}</td>
                  <td className="px-4 py-2">{m.entiteit ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.identificatie ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-kh-gray-600">{m.detail ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-kh-gray-700 mb-1">{label}</span>
      {children}
    </label>
  )
}

function Thead({ cols }: { cols: string[] }) {
  return (
    <thead className="bg-kh-gray-50 text-kh-gray-600 uppercase text-xs tracking-wider">
      <tr>{cols.map((c, i) => <th key={i} className="text-left px-4 py-3">{c}</th>)}</tr>
    </thead>
  )
}

function Box({ children }: { children: React.ReactNode }) {
  return <div className="card p-6 text-center text-kh-gray-500">{children}</div>
}
